import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { X } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import { useMe } from "@/shared/lib/session";
import { date } from "@/shared/lib/format";
import { Badge } from "@/shared/ui/Badge";
import { Modal } from "@/shared/ui/Modal";

// Messages from ClientOS, written in its back-office: announcements as banners, and What's
// new in the person's menu.

const announcementsKey = ["/v1/core/announcements"];
export const whatsNewKey = ["/v1/core/whats-new"];

/** Picks the Arabic or English of a message by the person's language. */
function useLocalized() {
  const { i18n } = useTranslation();
  return (ar: string, en: string) => (i18n.language === "ar" ? ar : en) || ar || en;
}

/** ClientOS's announcements for the person now, each closed for them by its ×. */
export function AnnouncementBanners() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const l = useLocalized();
  const { data } = useQuery({ queryKey: announcementsKey, queryFn: () => api.get<S["Announcement"][]>("/v1/core/announcements"), staleTime: 5 * 60_000 });
  const close = async (id: string) => {
    qc.setQueryData<S["Announcement"][]>(announcementsKey, (list) => list?.filter((a) => a.id !== id));
    await api.post(`/v1/core/announcements/${id}/dismiss`).catch(() => {});
  };
  return (data ?? []).map((a) => {
    const body = l(a.body_ar, a.body_en);
    return (
      <div key={a.id} role="status"
        className={clsx("flex items-start justify-center gap-3 px-4 py-1.5 text-[13px]", a.tone === "warning" ? "bg-saffron-soft text-ink" : "bg-info-soft text-info")}>
        <p className="min-w-0 text-center">
          <span className="font-medium">{l(a.title_ar, a.title_en)}</span>
          {body && <span> — {body}</span>}
          {a.link && <> <a href={a.link} target="_blank" rel="noreferrer" className="font-medium underline">{t("common.more")}</a></>}
        </p>
        {!me?.support && (
          <button type="button" className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100" aria-label={t("shell.closeAnnouncement")} onClick={() => void close(a.id)}>
            <X className="size-3.5" />
          </button>
        )}
      </div>
    );
  });
}

/** What's new in ClientOS, with how many entries are new to the person. */
export function useWhatsNew() {
  return useQuery({ queryKey: whatsNewKey, queryFn: () => api.get<S["WhatsNew"]>("/v1/core/whats-new"), staleTime: 10 * 60_000 });
}

/** The product's changes, newest first. Opening them marks them read; what was new keeps
 * its badge until the dialog closes. */
export function WhatsNewDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const l = useLocalized();
  const { data } = useWhatsNew();
  const unseen = data?.unseen ?? 0;
  useEffect(() => {
    if (unseen > 0 && !me?.support) void api.post("/v1/core/whats-new/seen").catch(() => {});
  }, [unseen, me?.support]);
  const close = () => {
    void qc.invalidateQueries({ queryKey: whatsNewKey });
    onClose();
  };
  const items = data?.items ?? [];
  return (
    <Modal open onClose={close} title={t("shell.whatsNew")} size="md">
      {items.length === 0 ? <p className="py-6 text-center text-ink-3">{t("shell.whatsNewEmpty")}</p> : (
        <ol className="space-y-5">
          {items.map((e) => (
            <li key={e.id}>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold">{l(e.title_ar, e.title_en)}</h3>
                {e.new && <Badge tone="green">{t("shell.isNew")}</Badge>}
              </div>
              <div className="text-[12px] text-ink-3">{date(e.published_at)}</div>
              {l(e.body_ar, e.body_en) && <p className="mt-1.5 leading-relaxed whitespace-pre-line text-ink-2">{l(e.body_ar, e.body_en)}</p>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
